import request from './request';
import { Message } from '../types';

const toStatus = (s: any): Message['status'] => {
  const v = String(s || 'sent').toLowerCase();
  switch (v) {
    case 'sent':
    case 'delivered':
    case 'read':
    case 'failed':
      return v as Message['status'];
    default:
      return 'sent';
  }
};

export const getMessagesApi = async (userId: string, params?: { since_id?: string }): Promise<Message[]> => {
  const config = params?.since_id ? { params: { since_id: params.since_id } } : {};
  const response = await request.get(`/api/chats/${userId}/messages`, config);
  return response.data.map((m: any) => {
    // 基础消息字段
    const baseMessage = {
      id: m.id,
      sender: m.sender,
      type: m.type,
      content: m.content,
      timestamp: m.timestamp,
      status: toStatus(m.status),
      wa_id: m.wa_id,
      quoted_message: m.quoted_message
        ? { id: m.quoted_message.id, sender: m.quoted_message.sender, content: m.quoted_message.content, wa_id: m.quoted_message.wa_id }
        : undefined,
    };

    // 如果有 interactive_payload，添加额外字段用于渲染
    if (m.interactive_payload) {
      const p = m.interactive_payload;
      const header = p?.header;
      const bodyText = p?.body?.text;
      const footerText = p?.footer?.text;
      const btn = p?.action?.parameters;
      const ctaHeader = header?.type === 'text' && header?.text
        ? { type: 'text' as const, content: header.text }
        : (header?.type === 'image' && header?.image?.link ? { type: 'image' as const, content: header.image.link } : undefined);
      const cta = (btn?.display_text && btn?.url) ? { displayText: btn.display_text, url: btn.url } : undefined;

      return {
        ...baseMessage,
        interactive_payload: p,  // 保留完整的 interactive_payload
        ctaHeader,
        ctaBody: bodyText,
        ctaFooter: footerText,
        cta
      };
    }

    return baseMessage;
  });
};

export const sendMessageApi = async (
  userId: string,
  messageData: { type: Message['type'] | 'interactive'; content: string | any; quotedMessageId?: string; interactivePayload?: any }
): Promise<Message> => {
  const response = await request.post(`/api/chats/${userId}/messages`, {
    type: messageData.type,
    content: messageData.content,
    quoted_message_id: messageData.quotedMessageId ?? null,
    interactive_payload: messageData.interactivePayload ?? null,
    location: messageData.type === 'location' ? messageData.content : null,
  });
  const m = response.data;
  return {
    id: m.id,
    sender: m.sender,
    type: m.type,
    content: m.content,
    timestamp: m.timestamp,
    status: toStatus(m.status),
    wa_id: m.wa_id,
    quoted_message: m.quoted_message
      ? { id: m.quoted_message.id, sender: m.quoted_message.sender, content: m.quoted_message.content, wa_id: m.quoted_message.wa_id }
      : undefined,
  };
};

export const clearChatHistoryApi = async (userId: string): Promise<{ success: boolean }> => {
  const response = await request.delete(`/api/chats/${userId}/messages`, { params: { confirm: true } });
  return response.data;
};

export const markMessagesReadApi = async (userId: string): Promise<{ success: boolean; updated: number }> => {
  const response = await request.post(`/api/chats/${userId}/messages/read`);
  return response.data;
};

/** 按保留期限清理所有用户的聊天记录及媒体文件（异步模式，返回 task_id） */
export const clearByRetentionInitiateApi = async (days: number): Promise<{
  task_id: string;
  status: string;
  message: string;
  progress?: { processed: number; total: number };
}> => {
  const response = await request.delete(`/api/chats/clear-by-retention`, {
    params: { days, confirm: true },
  });
  return response.data;
};

/** 查询清理任务进度 */
export const clearByRetentionStatusApi = async (taskId: string): Promise<{
  task_id: string;
  status: string;
  progress: { processed: number; total: number };
  result: { deleted_messages: number; deleted_files: number; cutoff_date: string };
  error: string | null;
}> => {
  const response = await request.get('/api/chats/clear-by-retention/status', {
    params: { task_id: taskId },
  });
  return response.data;
};
